import React from 'react';
import { ArrowRight } from 'lucide-react';
import Dialog from '../../ui/Dialog';
import { formatCount as n } from '../../lib/pipelineStages';
import type { PartKind, StageDestination, StageDetail } from '../../lib/stageDetail';
import { TONE_ICON, TONE_WORDS } from './PipelineFlow';

interface PipelineStageDialogProps {
  detail: StageDetail;
  onClose: () => void;
  /** Follows one of the stage's destinations: a feed already filtered, the flow or the sources. */
  onGo: (destination: StageDestination) => void;
}

/** What each kind of part is called beside its figure, so the colour of its segment is never the only sign. */
const KIND_WORDS = (kind: PartKind, next: string | null): string => {
  if (kind === 'on') return next ? `goes on to ${next}` : 'the end of the pipeline';
  if (kind === 'held') return 'stops here';
  if (kind === 'need') return 'waits on a person';
  return 'failed';
};

const percent = (value: number, whole: number) => (whole > 0 ? `${Math.round((value / whole) * 100)}%` : '—');

/**
 * One stage of the AI Analysis Pipeline, opened (docs/c3/DESIGN.md, Pipeline stage dialog). It says what the stage does,
 * where what came into it went (a bar and a line per part, from the counters the pipeline already shows), what waits
 * on a person, and how to follow it: the feed with a state or a type already chosen, the Alert Flow, the sources.
 */
const PipelineStageDialog: React.FC<PipelineStageDialogProps> = ({ detail, onClose, onGo }) => {
  const { input, parts, unaccounted, attention } = detail;
  // Said once: when a part of the bar already carries what waits or failed, the line under it would repeat it.
  const AttentionIcon = attention && !parts.some((part) => part.kind === attention.tone) ? TONE_ICON[attention.tone] : null;
  const hasInput = input.value > 0;
  // The first destination that has something to show leads; a destination with nothing is disabled.
  const primary = detail.destinations.findIndex((d) => !(d.kind === 'feed' && d.count === 0));
  return (
    <Dialog eyebrow={`Stage ${detail.index} of 7`} title={detail.title} size="md" onClose={onClose}>
      <div className="c3-stage">
        <p className="c3-stage-does">{detail.does}</p>

        <section className="c3-stage-section" aria-label="Where it went">
          <h4 className="c3-stage-h">
            <span className="c3-stage-total">{n(input.value)}</span> {input.label}
          </h4>
          {hasInput ? (
            <>
              <div
                className="c3-stage-bar"
                role="img"
                aria-label={parts.map((part) => `${n(part.value)} ${part.label}`).join(', ')}
              >
                {parts.map((part) => (
                  <span
                    key={part.key}
                    className={`c3-stage-seg is-${part.kind}`}
                    style={{ flexGrow: part.value }}
                  />
                ))}
                {unaccounted > 0 ? <span className="c3-stage-seg is-gap" style={{ flexGrow: unaccounted }} /> : null}
              </div>
              <ul className="c3-stage-parts">
                {parts.map((part) => {
                  const Icon = part.kind === 'need' ? TONE_ICON.need : part.kind === 'bad' ? TONE_ICON.bad : null;
                  return (
                    <li key={part.key} className={`c3-stage-part is-${part.kind}`}>
                      <span className="c3-stage-swatch" aria-hidden />
                      <span className="c3-stage-part-main">
                        <span className="c3-stage-part-name">
                          {Icon ? <Icon size={14} strokeWidth={1.5} aria-hidden /> : null}
                          <b>{n(part.value)}</b> {part.label}
                        </span>
                        <span className="c3-stage-part-where">{part.note ?? KIND_WORDS(part.kind, detail.next)}</span>
                      </span>
                      <span className="c3-stage-part-pct">{percent(part.value, input.value)}</span>
                    </li>
                  );
                })}
                {unaccounted > 0 ? (
                  <li className="c3-stage-part is-gap">
                    <span className="c3-stage-swatch" aria-hidden />
                    <span className="c3-stage-part-main">
                      <span className="c3-stage-part-name">
                        <b>{n(unaccounted)}</b> not accounted for
                      </span>
                      <span className="c3-stage-part-where">the counts do not add up yet</span>
                    </span>
                    <span className="c3-stage-part-pct">{percent(unaccounted, input.value)}</span>
                  </li>
                ) : null}
              </ul>
            </>
          ) : (
            <p className="c3-stage-empty">Nothing has reached this stage in this window yet.</p>
          )}
        </section>

        {attention && AttentionIcon ? (
          <p className={`c3-stage-attn is-${attention.tone}`}>
            <AttentionIcon size={16} strokeWidth={1.5} aria-hidden />
            <span>
              {attention.text}
              <span className="sr-only">{TONE_WORDS[attention.tone]}</span>
            </span>
          </p>
        ) : null}

        <p className="c3-stage-chain">
          {detail.previous ?? 'Connected sources'} <ArrowRight size={14} strokeWidth={1.5} aria-hidden />{' '}
          <b>{detail.title}</b> <ArrowRight size={14} strokeWidth={1.5} aria-hidden />{' '}
          {detail.next ?? 'Resolved'}
        </p>

        <div className="c3-stage-actions">
          {detail.destinations.map((destination, i) => {
            const count = destination.kind === 'feed' ? destination.count : undefined;
            const empty = count === 0;
            return (
              <button
                key={destination.label}
                type="button"
                className={`c3-btn${i === primary ? ' c3-btn-primary' : ''}`}
                disabled={empty}
                title={empty ? 'Nothing to show for this in this window' : undefined}
                onClick={() => onGo(destination)}
              >
                {destination.label}
                {count != null ? <span className="c3-stage-count"> · {n(count)}</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
};

export default PipelineStageDialog;
